import React, { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Container,
  Divider,
  Grid,
  IconButton,
  InputAdornment,
  Paper,
  TextField,
  Typography
} from '@mui/material'
import { ArrowBack, Close, LocalOffer } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import { useCart } from '../../contexts/CartContext'
import PaymentMethodSelector from '../payment/PaymentMethodSelector'
import CreditCardForm from '../payment/CreditCardForm'
import PixPayment from '../payment/PixPayment'
import CheckoutSummary from './CheckoutSummary'
import { apiService } from '../../services/api'
import { paymentService } from '../../services/paymentService'
import type { CardData, CreditCardData, PaymentMethod } from '../../types/cart'
import type { Cupom } from '../../types/cupons'
import { parseBigDecimal } from '../../utils/bigDecimal'
import LoginDialog from '../auth/LoginDialog'

const CheckoutPage: React.FC = () => {
  const navigate = useNavigate()
  const { state, clearCart } = useCart()
  const { user, isAuthenticated } = useAuth()

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('PIX_DIRETO')
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState('')
  const [step, setStep] = useState<'payment' | 'success'>('payment')
  const [successMessage, setSuccessMessage] = useState('')

  const [couponInput, setCouponInput] = useState('')
  const [coupon, setCoupon] = useState<Cupom | null>(null)
  const [couponError, setCouponError] = useState('')
  const [isValidatingCoupon, setIsValidatingCoupon] = useState(false)
  const [loginOpen, setLoginOpen] = useState(false)

  const subtotal = state.total

  const computeDiscount = (appliedCoupon: Cupom | null): number => {
    if (!appliedCoupon) return 0

    const applicableTotal =
      appliedCoupon.pacoteIds && appliedCoupon.pacoteIds.length > 0
        ? state.items
            .filter((item) => appliedCoupon.pacoteIds.includes(item.pacote.id))
            .reduce((sum, item) => sum + item.pacote.preco * item.quantity, 0)
        : subtotal

    if (applicableTotal <= 0) return 0

    let discount = 0
    if (appliedCoupon.tipoDesconto === 'PERCENTUAL') {
      const limite = parseBigDecimal(appliedCoupon.limiteValorAplicacao)
      const base = limite != null && limite > 0 ? Math.min(applicableTotal, limite) : applicableTotal
      discount = base * (appliedCoupon.valor / 100)
    } else if (appliedCoupon.tipoDesconto === 'VALOR_FIXO' || appliedCoupon.tipoDesconto === 'FIXO') {
      discount = appliedCoupon.valor
    }

    discount = Math.min(applicableTotal, discount)
    return Math.max(0, Math.round(discount * 100) / 100)
  }

  const discount = computeDiscount(coupon)
  const totalToPay = Math.max(0, Math.round((subtotal - discount) * 100) / 100)

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

  const handleApplyCoupon = async () => {
    const code = couponInput.trim().toUpperCase()
    if (!code) return
    setIsValidatingCoupon(true)
    setCouponError('')
    setError('')
    try {
      const response = await apiService.buscarCupomPorCodigo(code)
      if (response?.success && response.data?.codigo) {
        setCoupon(response.data)
        setCouponInput(response.data.codigo)
      } else {
        setCoupon(null)
        setCouponError('Cupom inválido')
      }
    } catch (err: any) {
      setCoupon(null)
      setCouponError(err?.message || 'Cupom inválido')
    } finally {
      setIsValidatingCoupon(false)
    }
  }

  const handleRemoveCoupon = () => {
    setCoupon(null)
    setCouponInput('')
    setCouponError('')
  }

  const handlePixPayment = async () => {
    setIsProcessing(true)
    setError('')
    try {
      const pacoteIds = state.items.flatMap((item) => Array.from({ length: item.quantity }, () => item.pacote.id))
      const paymentData = {
        userId: user?.userId || '',
        pacoteIds,
        tipoPagamento: 'PIX_DIRETO' as const,
        cupomCodigo: coupon?.codigo
      }

      const response = await apiService.createPixPayment(paymentData)
      const linkVisualizacao = response.data?.linkVisualizacao || response.dados?.linkVisualizacao

      if (linkVisualizacao) {
        const popup = window.open(linkVisualizacao, '_blank')
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          setSuccessMessage(`Pagamento PIX iniciado! Abra o link para finalizar: ${linkVisualizacao}`)
        } else {
          setSuccessMessage('Pagamento PIX iniciado! Uma nova janela foi aberta para finalizar o pagamento.')
        }
      } else {
        setSuccessMessage('Pagamento PIX iniciado! Aguarde a confirmação para o crédito do pacote.')
      }

      clearCart()
      setStep('success')
    } catch (e: any) {
      setError(e?.message || 'Erro ao processar pagamento PIX.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleCardPayment = async (cardData: CardData, creditCardData: CreditCardData) => {
    setIsProcessing(true)
    setError('')
    try {
      if (!paymentService.isScriptLoaded()) {
        throw new Error('Biblioteca de pagamento não está carregada. Recarregue a página e tente novamente.')
      }

      const paymentToken = await paymentService.generatePaymentToken(cardData)
      const userId = user?.userId || ''
      const pacoteIds = state.items.flatMap((item) => Array.from({ length: item.quantity }, () => item.pacote.id))

      const response = await apiService.createCardPayment(
        userId,
        pacoteIds,
        coupon?.codigo,
        creditCardData,
        paymentToken.payment_token
      )

      const paymentUrl = response.dados?.paymentUrl || response.data?.paymentUrl
      if (paymentUrl) window.open(paymentUrl, '_blank')

      clearCart()
      setSuccessMessage('Pagamento processado! Se necessário, finalize no link aberto.')
      setStep('success')
    } catch (e: any) {
      setError(e?.message || 'Erro ao processar pagamento com cartão.')
    } finally {
      setIsProcessing(false)
    }
  }

  if (state.items.length === 0 && step !== 'success') {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={900} gutterBottom>
            Carrinho vazio
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Adicione pacotes antes de finalizar a compra.
          </Typography>
          <Button variant="contained" startIcon={<ArrowBack />} onClick={() => navigate('/')}>
            Voltar
          </Button>
        </Paper>
      </Container>
    )
  }

  if (!isAuthenticated && step !== 'success') {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" fontWeight={900} gutterBottom>
            Faça login para continuar
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Para finalizar o pagamento, você precisa estar logado.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/')}>
              Voltar
            </Button>
            <Button variant="contained" onClick={() => setLoginOpen(true)}>
              Entrar
            </Button>
          </Box>
        </Paper>

        <LoginDialog open={loginOpen} onClose={() => setLoginOpen(false)} redirectTo="/checkout" />
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 5 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={900}>
            Checkout
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Finalize a compra do seu pacote Palworld.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<ArrowBack />} onClick={() => navigate('/')}>
          Continuar comprando
        </Button>
      </Box>

      {step === 'success' ? (
        <Paper sx={{ p: 4 }}>
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage || 'Tudo certo!'}
          </Alert>
          <Button variant="contained" onClick={() => navigate('/')}>
            Voltar para Home
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <PaymentMethodSelector selectedMethod={paymentMethod} onMethodChange={setPaymentMethod} totalAmount={totalToPay} />

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" fontWeight={900} sx={{ mb: 1.5 }}>
                Cupom
              </Typography>
              <TextField
                fullWidth
                value={couponInput}
                onChange={(e) => setCouponInput(e.target.value)}
                placeholder="Digite o código do cupom"
                error={!!couponError}
                helperText={couponError || ' '}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LocalOffer />
                    </InputAdornment>
                  ),
                  endAdornment: coupon ? (
                    <InputAdornment position="end">
                      <IconButton onClick={handleRemoveCoupon} size="small">
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : undefined
                }}
              />
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Button variant="outlined" onClick={handleApplyCoupon} disabled={isValidatingCoupon || !couponInput.trim()}>
                  {isValidatingCoupon ? 'Validando...' : coupon ? 'Cupom aplicado' : 'Aplicar'}
                </Button>
              </Box>

              <Divider sx={{ my: 3 }} />

              {paymentMethod === 'PIX_DIRETO' ? (
                <PixPayment onSubmit={handlePixPayment} isProcessing={isProcessing} totalAmount={totalToPay} />
              ) : (
                <CreditCardForm onSubmit={handleCardPayment} isProcessing={isProcessing} totalAmount={totalToPay} />
              )}

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  Total: {formatCurrency(totalToPay)} {discount > 0 ? `(desconto ${formatCurrency(discount)})` : ''}
                </Typography>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <CheckoutSummary subtotal={subtotal} discount={discount} total={totalToPay} coupon={coupon} />
          </Grid>
        </Grid>
      )}
    </Container>
  )
}

export default CheckoutPage
